import { useQuery } from "@tanstack/react-query";
import { getTokenInfo } from "@/lib/getTokenInfo";
import { tokenIndexes } from "@/constants/project-specific/tokens";
import type { ChainType } from "@/constants/chain-specific/chains";
import type { RequiredAggregatorChainType } from "@/constants/project-specific";
import type { Address } from "viem";
import { getTokenPlaceholderDataUrl } from "@/util/images/tokenPlaceholder";
interface TokenLogoData {
  logo?: string;
  name?: string;
  symbol?: string;
  source: 'local' | 'api' | 'fallback';
}

/**
 * Get token logo with smart caching and fallback strategy
 * 1. Check local token index first (instant)
 * 2. Fallback to API call with caching (async)
 * 3. Fallback to default logo if all else fails
 */
export function useTokenLogo(chainId: ChainType | undefined, tokenAddress: Address | undefined, options?: {
  enabled?: boolean;
  staleTime?: number;
}) {
  const {
    enabled = true,
    staleTime = 1000 * 60 * 60
  } = options || {}; // 1 hour default

  return useQuery({
    queryKey: ['tokenLogo', chainId, tokenAddress?.toLowerCase()],
    queryFn: async (): Promise<TokenLogoData> => {
      if (!chainId || !tokenAddress) {
        throw new Error('chainId and tokenAddress required');
      }

      // Step 1: Check local token indexes first (instant lookup)
      const localToken = findLocalToken(chainId as RequiredAggregatorChainType, tokenAddress);
      if (localToken?.logo) {
        console.log(`[TokenLogo] Found local logo for ${tokenAddress} on chain ${chainId}`);
        return {
          logo: localToken.logo,
          name: localToken.name,
          symbol: localToken.symbol,
          source: 'local'
        };
      }

      // Step 2: Fallback to API call
      try {
        console.log(`[TokenLogo] Fetching API logo for ${tokenAddress} on chain ${chainId}`);
        const tokenInfo = await getTokenInfo(chainId, tokenAddress);
        if (tokenInfo?.logo) {
          return {
            logo: tokenInfo.logo,
            name: tokenInfo.name,
            symbol: tokenInfo.symbol,
            source: 'api'
          };
        }
      } catch (error) {
        console.warn(`[TokenLogo] API fetch failed for ${tokenAddress}:`, error);
      }

      // Step 3: Fallback to default placeholder
      console.log(`[TokenLogo] Using fallback logo for ${tokenAddress}`);
      return {
        logo: getTokenPlaceholderDataUrl({
          size: 64,
          variant: 'symbol',
          symbol: ' ',
          bgColor: '#2F3136',
          fgColor: '#9AA0A6',
          shape: 'circle'
        }),
        source: 'fallback'
      };
    },
    enabled: enabled && !!chainId && !!tokenAddress,
    staleTime,
    // Cache for specified time
    retry: 1,
    // Only retry once for API failures
    retryDelay: 2000 // 2 second delay before retry
  });
}

/**
 * Batch hook for fetching multiple token logos efficiently
 */
export function useTokenLogos(tokens: Array<{
  chainId: ChainType;
  address: Address;
}>, options?: {
  enabled?: boolean;
  staleTime?: number;
}) {
  const queries = tokens.map(({
    chainId,
    address
  }) => ({
    queryKey: ['tokenLogo', chainId, address?.toLowerCase()],
    queryFn: async (): Promise<TokenLogoData> => {
      if (!chainId || !address) {
        throw new Error('chainId and address required');
      }

      // Use same logic as single token hook
      const localToken = findLocalToken(chainId as RequiredAggregatorChainType, address);
      if (localToken?.logo) {
        return {
          logo: localToken.logo,
          name: localToken.name,
          symbol: localToken.symbol,
          source: 'local'
        };
      }
      try {
        const tokenInfo = await getTokenInfo(chainId, address);
        if (tokenInfo?.logo) {
          return {
            logo: tokenInfo.logo,
            name: tokenInfo.name,
            symbol: tokenInfo.symbol,
            source: 'api'
          };
        }
      } catch (error) {
        console.warn(`[TokenLogos] API fetch failed for ${address}:`, error);
      }
      return {
        logo: getTokenPlaceholderDataUrl({
          size: 64,
          variant: 'question',
          bgColor: '#2F3136',
          fgColor: '#9AA0A6',
          shape: 'circle'
        }),
        source: 'fallback'
      };
    },
    enabled: options?.enabled !== false && !!chainId && !!address,
    staleTime: options?.staleTime ?? 1000 * 60 * 60,
    retry: 1,
    retryDelay: 2000
  }));
  return queries;
}

/**
 * Helper to find token in local indexes
 */
function findLocalToken(chainId: RequiredAggregatorChainType, tokenAddress: Address) {
  const tokenIndex = tokenIndexes[chainId];
  if (!tokenIndex) return null;

  // Search through all tokens in the index
  for (const [key, token] of Object.entries(tokenIndex)) {
    if (token.address.toLowerCase() === tokenAddress.toLowerCase()) {
      return token;
    }
  }
  return null;
}

/**
 * Get default token logo based on chain
 */
function getDefaultTokenLogo(chainId: ChainType): string {
  const defaults: Record<number, string> = {
    109: 'https://raw.githubusercontent.com/ChewySwap/chewy-tokenlist/main/tokens/bone.webp',
    // Shibarium
    2000: 'https://raw.githubusercontent.com/ChewySwap/chewy-tokenlist/main/tokens/dogecoin.webp',
    // Dogechain
    1: 'https://raw.githubusercontent.com/ChewySwap/chewy-tokenlist/main/tokens/eth.webp',
    // Ethereum
    8453: 'https://d2f70xi62kby8n.cloudfront.net/bridge/icons/networks/base.svg?auto=compress%2Cformat',
    // Base
    11155111: 'https://raw.githubusercontent.com/ChewySwap/chewy-tokenlist/main/tokens/eth.webp' // Sepolia
  };
  return defaults[chainId as number] || 'https://raw.githubusercontent.com/ChewySwap/chewy-tokenlist/main/tokens/unknown.webp';
}

/**
 * Preload token logos for better UX
 */
export function preloadTokenLogos(queryClient: any, tokens: Array<{
  chainId: ChainType;
  address: Address;
}>) {
  tokens.forEach(({
    chainId,
    address
  }) => {
    queryClient.prefetchQuery({
      queryKey: ['tokenLogo', chainId, address?.toLowerCase()],
      queryFn: async () => {
        const localToken = findLocalToken(chainId as RequiredAggregatorChainType, address);
        if (localToken?.logo) {
          return {
            logo: localToken.logo,
            name: localToken.name,
            symbol: localToken.symbol,
            source: 'local'
          };
        }
        try {
          const tokenInfo = await getTokenInfo(chainId, address);
          if (tokenInfo?.logo) {
            return {
              logo: tokenInfo.logo,
              name: tokenInfo.name,
              symbol: tokenInfo.symbol,
              source: 'api'
            };
          }
        } catch (error) {
          // Silent fail for preloading
        }
        return {
          logo: getDefaultTokenLogo(chainId),
          source: 'fallback'
        };
      },
      staleTime: 24 * 60 * 60 * 1000 // 24 hours
    });
  });
}